


import commentApi from '@/apis/comment';
import userApi from '@/apis/user';
import { Kilopost } from 'src/models/apis/movie/movieResponse';
import {
  COMMENT_ACCEPTED_FILE_TYPES,
  COMMENT_ATTACHMENT_FILE_MAX_MB,
  getNearKps,
  getLocationDispOfKp,
  validateAngle,
  validateComment,
  convertFileObjsToCandidateFiles,
} from '@/lib/commentHelper';
import {
  MAX_SAVABLE_FILES_COUNT,
  convertFilesToFileObjs,
  fetchFileAsObjectInfo,
  FileObj,
} from '@/lib/fileHelper';
import {
  defineComponent,
  reactive,
  onMounted,
  onBeforeUnmount,
  computed,
} from '@vue/composition-api';
import { AdminCommentIndexRequest } from 'src/models/apis/comment/adminCommentRequest';
import { AdminCommentIndexResponse } from 'src/models/apis/comment/adminCommentResponse';
import { Comment } from 'src/models/apis/comment/commentResponse';
import { CandidateFile } from '@/models';
import { getErrorMessages } from '@/lib/errMsgHelper';
import { AxiosError } from 'axios';
import useMaster from '@/composables/useMaster';
import { lineBreakToBR, splitByLineBreak } from '@/lib/utils';
import { AdminUser } from '@/models/apis/user/adminUserResponse';
import { GIKilopost } from '@/models/geoItem';
import PreviewFilesArea from '@/components/lib/PreviewFilesArea/index.vue';
import { AdminListTableField } from '@/types/adminListTable';
import { dtFormat } from '@/lib/dateHelper';

type SearchParams = AdminCommentIndexRequest;

type CommentDisp = Comment & {
  contentDisp: string;
};

interface AdminCommentsEdit {
  id: number;
  comment_type: string;
  content: string | null;
  user_id: number | null;
  share_scope: number | null;
  angle: number | null;
  kpObj?: Kilopost;
  update_ts: boolean;
  contentTextareaRows?: number;
  kp_uid: string | null;
  lat?: string;
  lon?: string;
  saveFiles: FileObj[];
  removeFileIds: number[];
}

interface AdminCommentsTableList {
  ts: string;
  commentTypeDisp: string;
  shareScopeLabel: string;
  user_display_name: string;
  contentDisp: string;
  kpInfo?: string;
  angleDisp?: string;
  hasFile: string;
}

interface AdminCommentsState {
  showEditModal: boolean;
  editMode: 'edit' | 'create';
  showDestroyModal: boolean;
  showErrorModal: boolean;
  candidateFiles: CandidateFile[];
  search: SearchParams;
  editing: AdminCommentsEdit;
  filteredComments: CommentDisp[];
  users: AdminUser[];
  errorMsgs: string[];
}

export default defineComponent({
  name: 'admin-comments',
  setup() {
    const now = new Date();
    const initSearchState = (): SearchParams => {
      return {
        comment_type: null,
        user_id: null,
        dt_from: new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()),
        dt_to: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
        content: null,
        share_scope: null,
      };
    };
    const initEditingState = (): AdminCommentsEdit => {
      return {
        id: 0,
        comment_type: '',
        content: null,
        user_id: null,
        share_scope: null,
        angle: null,
        kpObj: {} as Kilopost,
        update_ts: false,
        kp_uid: null,
        saveFiles: [],
        removeFileIds: [],
      };
    };
    const state = reactive<AdminCommentsState>({
      showEditModal: false,
      editMode: 'edit',
      showDestroyModal: false,
      showErrorModal: false,
      candidateFiles: [],
      search: initSearchState(),
      editing: initEditingState(),
      filteredComments: [],
      users: [],
      errorMsgs: [],
    });
    const { state: msts } = useMaster();

    const resetSearchResults = () => {
      state.filteredComments = [];
    };
    const convComments = (data: AdminCommentIndexResponse): CommentDisp[] => {
      const results = data.map<CommentDisp>(e => {
        return {
          ...e,
          content: splitByLineBreak(e.content).join('\n'),
          contentDisp: lineBreakToBR(e.content),
        };
      });
      return results;
    };
    const doSearch = () => {
      resetSearchResults();
      const params = state.search;
      const reqParams = Object.assign({}, params);
      if (params.dt_to) {
        reqParams.dt_to = new Date(params.dt_to.valueOf() + 86400 * 1000);
      }
      commentApi.adminIndex(reqParams)
        .then(({ data }) => {
          if (!data || data.length === 0) {
            return;
          }
          state.filteredComments = convComments(data);
        });
    };

    onMounted(async() => {
      const [, { data }] = await Promise.all([
        window.master.$promise,
        userApi.adminIndex({}),
      ]);
      msts.commentTypes = window.master.commentType.vals;
      msts.shareScopes = window.master.lovs.share_scope.vals;
      msts.kpMap = window.master.kpMap;
      msts.roadNameDispMap = window.master.roadNameDispMap;
      state.users = data;
      doSearch();
    });

    const releaseAllFiles = () => {
      state.editing.saveFiles.forEach(elm => URL.revokeObjectURL(elm.content));
      state.editing.saveFiles = [];
      state.editing.removeFileIds = [];
      state.candidateFiles.forEach(elm => URL.revokeObjectURL(elm.content));
      state.candidateFiles = [];
    };

    onBeforeUnmount(() => {
      releaseAllFiles();
    });

    const getCommentTypeDisp = (type: string): string => {
      for (const commentType of msts.commentTypes) {
        if (commentType.comment_type === type) {
          return commentType.comment_type_disp;
        }
      }
      return type;
    };
    const getShareScopeLabel = (scope: number): string => {
      for (const shareScope of msts.shareScopes) {
        if (shareScope.key === scope) {
          return shareScope.val;
        }
      }
      return scope.toString();
    };
    const getKpInfo = (kpUid: string | null): string | undefined => {
      if (!kpUid) {
        return;
      }
      const kps = Array.from(msts.kpMap.values())
        .map(e => Array.from(e.values())).flat(2);
      const kpObj = kps.find(e => e.kp_uid === kpUid);
      if (!kpObj) {
        return;
      }
      return getLocationDispOfKp(kpObj, msts.roadNameDispMap);
    };

    const displayData = computed<AdminCommentsTableList[]>(() => {
      return state.filteredComments.map(comment => {
        return {
          ...comment,
          commentTypeDisp: getCommentTypeDisp(comment.comment_type),
          shareScopeLabel: getShareScopeLabel(comment.share_scope),
          kpInfo: getKpInfo(comment.kp_uid),
          angleDisp: comment.angle === null || comment.angle === undefined ? '' : `${comment.angle}°`,
          hasFile: comment.comment_files.length > 0 ? '有' : '',
        };
      });
    });
    const canSave = computed<boolean>(() => {
      const hasNoError = state.errorMsgs.length === 0;
      return validateComment(state.editing) && hasNoError;
    });
    const nearKpThresholdMeters = 100;
    const nearKps = computed<GIKilopost[]>(() => {
      if (!state.editing.lat || !state.editing.lon) {
        return [];
      }
      return getNearKps(
        {lat: state.editing.lat, lon: state.editing.lon},
        msts.kpMap,
        msts.roadNameDispMap,
        nearKpThresholdMeters,
      );
    });
    const editItemHasValidAngle = computed<boolean>(() => {
      return validateAngle(state.editing.angle);
    });
    const sortedPreviewFiles = computed<CandidateFile[]>(() => {
      return state.candidateFiles.sort((a, b) => a.id - b.id);
    });

    const save = async() => {
      try {
        if (state.editMode === 'edit') {
          await commentApi.adminUpdate(state.editing.id, {
            lat: state.editing.lat ?? '',
            lon: state.editing.lon ?? '',
            share_scope: state.editing.share_scope || 0,
            comment_type: state.editing.comment_type,
            content: state.editing.content || '',
            kp_uid: state.editing.kp_uid,
            angle: state.editing.angle,
            update_ts: state.editing.update_ts,
            save_files: state.editing.saveFiles.map(e => e.file),
            remove_file_ids: state.editing.removeFileIds,
          });
          state.errorMsgs = [];
          state.showEditModal = false;
          releaseAllFiles();
          doSearch();
        }
      } catch (err) {
        state.errorMsgs = getErrorMessages(err as AxiosError);
      }
    };

    const edit = async(obj: Comment) => {
      const minRows = 4;
      let rows = splitByLineBreak(obj.content).length;
      rows = Math.max(rows, minRows);

      state.editing = {
        id: obj.id,
        user_id: obj.user_id,
        comment_type: obj.comment_type,
        content: obj.content,
        contentTextareaRows: rows,
        share_scope: obj.share_scope,
        angle: obj.angle,
        kp_uid: obj.kp_uid,
        lat: obj.lat,
        lon: obj.lon,
        update_ts: false,
        saveFiles: [],
        removeFileIds: [],
      };
      releaseAllFiles();
      state.errorMsgs = [];
      state.candidateFiles = [];

      obj.comment_files.forEach((file) => {
        fetchFileAsObjectInfo(file.file_path).then((savedFileInfo) => {
          if (!savedFileInfo || !savedFileInfo.url || !savedFileInfo.type) return;
          state.candidateFiles.push({
            id: file.id,
            content: savedFileInfo.url,
            type: savedFileInfo.type,
            name: file.file_name ?? '',
            path: file.file_path,
            selected: true,
          });
        });
      });

      state.showEditModal = true;
      state.editMode = 'edit';
    };

    const tryDestroy = (obj: Comment) => {
      state.editing.id = obj.id;
      state.showDestroyModal = true;
    };
    const doDestroy = async() => {
      try {
        await commentApi.adminDestroy(state.editing.id);
        state.showDestroyModal = false;
        doSearch();
      } catch (err) {
        state.showDestroyModal = false;
        state.showErrorModal = true;
      }
    };

    const cancelEdit = () => {
      releaseAllFiles();
      state.showEditModal = false;
    };
    const handleFileUpload = async(fileList: FileList) => {
      state.errorMsgs = [];
      if (!fileList) return;
      const uploadFilesCount = fileList.length + state.candidateFiles.length;
      try {
        if (uploadFilesCount > MAX_SAVABLE_FILES_COUNT) {
          throw Error('添付ファイルの最大数は' + MAX_SAVABLE_FILES_COUNT + '個です。');
        }
        // 新規ファイルのidを連番で割り振る(実際のidはサーバー側で振られる)
        const maxId = state.candidateFiles.map(e => e.id).reduce((a, b) => Math.max(a, b), 0);
        const fileObjs = await convertFilesToFileObjs(fileList, maxId, COMMENT_ATTACHMENT_FILE_MAX_MB);
        state.candidateFiles.push(...convertFileObjsToCandidateFiles(fileObjs));
        state.editing.saveFiles.push(...fileObjs);
      } catch (error) {
        state.errorMsgs.push(...getErrorMessages(error as AxiosError));
      }
    };
    const removeFile = (fileId: number) => {
      state.candidateFiles = state.candidateFiles.filter(e => e.id !== fileId);
      const saveFile = state.editing.saveFiles.find(e => e.id === fileId);
      if (saveFile) {
        state.editing.saveFiles = state.editing.saveFiles.filter(e => e.id !== fileId);
        return;
      }
      state.editing.removeFileIds.push(fileId);
    };

    const fields: AdminListTableField[] = [
      { name: 'ts', label: '最終更新日時', type: 'convFunc', convFunc: (srcString: string) => dtFormat(srcString, 'yyyy/mm/dd HH:MM') },
      { name: 'commentTypeDisp', label: '種別' },
      { name: 'kpInfo', label: '場所' },
      { name: 'angleDisp', label: '方角' },
      { name: 'shareScopeLabel', label: '公開範囲' },
      { name: 'user_display_name', label: '作成者' },
      { name: 'contentDisp', label: '内容', type: 'html' },
      { name: 'hasFile', label: '添付' },
    ];
    const editModalTitle = '編集';
    const destroyModalTitle = '削除';
    const destroyModalMsg = 'この付箋を削除してもよろしいですか？';
    const errorModalTitle = 'エラー';
    const errorModalMsg = '不明なエラーです。ネットワークの状態をご確認いただき再度お試し下さい。';

    return {
      state,
      msts,
      COMMENT_ACCEPTED_FILE_TYPES,
      // methods:
      doSearch,
      edit,
      tryDestroy,
      doDestroy,
      cancelEdit,
      save,
      handleFileUpload,
      removeFile,
      // computed:
      displayData,
      canSave,
      nearKps,
      editItemHasValidAngle,
      sortedPreviewFiles,
      // others:
      fields,
      editModalTitle,
      destroyModalTitle,
      destroyModalMsg,
      errorModalTitle,
      errorModalMsg,
    };
  },
  components: {
    PreviewFilesArea,
  },
});
